import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';

import { API_URL } from '../lib/api';
import logger from '../lib/logger';

const AuthContext = createContext(null);

// Setup axios interceptor for automatic token refresh
let isRefreshing = false;
let failedQueue = [];
let isLoggingOut = false; // Prevent interceptor from refreshing during logout

const processQueue = (error) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

// Create axios interceptor that auto-refreshes on 401
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // CRITICAL: Skip refresh if we're in the middle of logging out
    if (isLoggingOut) {
      return Promise.reject(error);
    }
    
    // If 401 and not a refresh/logout/login request, try to refresh token
    if (error.response?.status === 401 && 
        !originalRequest._retry && 
        !originalRequest.url?.includes('/auth/refresh') &&
        !originalRequest.url?.includes('/auth/login') &&
        !originalRequest.url?.includes('/auth/logout') &&
        !originalRequest.url?.includes('/auth/me')) {
      
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return axios(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        processQueue(null);
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        // If refresh fails, redirect to login
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/respond/')) {
          window.location.href = '/';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const hasCheckedAuth = useRef(false);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      setAuthReady(true);
    } catch (error) {
      // On Safari, sometimes cookies aren't sent immediately - retry once
      if (!hasCheckedAuth.current) {
        hasCheckedAuth.current = true;
        try {
          // Small delay for Safari cookie processing
          await new Promise(resolve => setTimeout(resolve, 100));
          const retryResponse = await axios.get(`${API_URL}/api/auth/me`, {
            withCredentials: true
          });
          setUser(retryResponse.data);
          setAuthReady(true);
          return;
        } catch (retryError) {
          // Retry also failed, proceed with normal error handling
        }
      }
      setUser(false);
      setAuthReady(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // When the authenticated user is loaded, sync their preferred_language to the
  // local UI — but ONLY if the user hasn't explicitly chosen a language locally.
  // This makes Danish profiles see Danish UI on a fresh browser/incognito session.
  useEffect(() => {
    if (!user || typeof user !== 'object') return;
    const explicit = localStorage.getItem('lifemap_language_explicit');
    if (explicit === '1') return;  // user manually picked, respect it

    // Map current_country → language fallback for users whose stored
    // preferred_language hasn't caught up with their location.
    const COUNTRY_TO_LANG = {
      denmark: 'da', danmark: 'da', dk: 'da',
      germany: 'de', deutschland: 'de', de: 'de',
      france: 'fr', frankreich: 'fr', fr: 'fr',
    };
    const country = (user.current_country || '').toLowerCase().trim();
    const profileLang = user.preferred_language;
    const countryLang = COUNTRY_TO_LANG[country];

    // Pick the best candidate: country wins if it's a stronger non-English
    // signal; otherwise honor the stored preferred_language.
    let target = null;
    if (countryLang && countryLang !== 'en') {
      target = countryLang;
    } else if (profileLang) {
      target = profileLang;
    }
    if (!target) return;

    const current = localStorage.getItem('lifemap_language');
    if (current === target) return;  // already synced

    window.dispatchEvent(new CustomEvent('language-sync-from-profile', {
      detail: target,
    }));
  }, [user]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, 
      { email, password },
      { withCredentials: true }
    );
    setUser(response.data);
    setAuthReady(true);
    return response.data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const response = await axios.post(`${API_URL}/api/auth/register`,
      { email, password, name },
      { withCredentials: true }
    );
    setUser(response.data);
    setAuthReady(true);
    return response.data;
  }, []);

  const logout = useCallback(async () => {
    // Set flag BEFORE calling API to prevent interceptor from auto-refreshing
    isLoggingOut = true;
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      logger.error('Logout error:', error);
    }
    // Clear user state
    setUser(false);
    setAuthReady(false);
    hasCheckedAuth.current = false;
    // Reset flag after a delay to ensure navigation completes
    setTimeout(() => {
      isLoggingOut = false;
    }, 500);
  }, []);

  const refreshToken = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
      await checkAuth();
    } catch (error) {
      setUser(false);
    }
  }, [checkAuth]);

  const updateUser = useCallback((data) => {
    setUser(prev => ({ ...prev, ...data }));
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    loading,
    authReady,
    login,
    register,
    logout,
    refreshToken,
    updateUser,
    checkAuth
  // Functions are stable via useCallback, only data deps trigger re-memo
  }), [user, loading, authReady, login, register, logout, refreshToken, updateUser, checkAuth]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
